import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type AuditEntry, type AuditKind, auditParams, useAuditLog } from '../../api/admin';
import { FilterChips } from '../../components/FilterChips';
import { ErrorView } from '../../components/Guards';
import { dateLocale } from '../../i18n';
import { formatDate } from '../../lib/format';
import ui from '../../styles/ui.module.css';
import styles from './AdminPages.module.css';

const KINDS: AuditKind[] = ['VIEW', 'ACCOUNT', 'CHOIR', 'DELETION', 'SIGN_IN'];
const PERIODS = [7, 30, 365, 0];

type T = (key: string, options?: Record<string, unknown>) => string;
type Details = Record<string, unknown>;

function firstName(name: string | null): string {
  return name?.split(' ')[0] ?? '';
}

/** A part and roles as written by the server: { part, roles }. */
function describeMembership(value: unknown, t: T): string {
  if (!value || typeof value !== 'object') return '–';
  const { part, roles } = value as { part?: string | null; roles?: string[] };
  const words = (roles ?? []).map((r) => t(`roles.${r}`));
  if (part) words.push(t(`parts.${part}`));
  return words.join(', ') || '–';
}

/** One line of details for an entry, in the interface language. */
export function auditDetails(entry: AuditEntry, t: T): string {
  let d: Details;
  try {
    d = JSON.parse(entry.details) as Details;
  } catch {
    return '–';
  }
  const locale = dateLocale();
  const str = (key: string) => (d[key] == null ? '' : String(d[key]));
  switch (entry.action) {
    case 'VIEW_AS_STARTED':
      return d.reason ? t('admin.audit.details.reason', { reason: str('reason') }) : '–';
    case 'VIEW_AS_ENDED':
      return t('admin.audit.details.ended', { count: Number(d.minutes ?? 0), how: t(`admin.audit.how.${str('how') || 'END_VIEWING'}`) });
    case 'SCREEN_VIEWED':
      return str('path');
    case 'SUBMISSION_OPENED':
    case 'SUBMISSION_PLAYED':
      return d.song
        ? t('admin.audit.details.submission', { song: str('song'), date: formatDate(str('submittedAt'), locale) })
        : '–';
    case 'MEMBERSHIP_ADDED':
      return `${str('choir')} · ${describeMembership(d.after, t)}`;
    case 'MEMBERSHIP_CHANGED':
      return `${str('choir')} · ${describeMembership(d.before, t)} → ${describeMembership(d.after, t)}`;
    case 'MEMBERSHIP_REMOVED':
      return str('choir');
    case 'PASSWORD_LINK':
      return t(d.purpose === 'SETUP' ? 'admin.audit.details.setupLink' : 'admin.audit.details.resetLink', { count: Number(d.validDays ?? 7) });
    case 'ADMIN_CREATED':
    case 'ADMIN_SETUP_RESET':
      return t('admin.audit.details.adminLink', { count: Number(d.validHours ?? 24) });
    case 'ACCOUNT_CREATED':
      return str('email');
    case 'ACCOUNT_EDITED':
      return (['name', 'email', 'locale'] as const)
        .filter((k) => d[k])
        .map((k) => {
          const change = d[k] as { from: string; to: string };
          const show = (v: string) => (k === 'locale' ? t(`languages.${v}`, { defaultValue: v }) : v);
          return `${t(`admin.audit.fields.${k}`)}: ${show(change.from)} → ${show(change.to)}`;
        })
        .join(' · ');
    case 'CHOIR_CREATED':
      return `${str('choir')} · ${t(d.newDirector ? 'admin.audit.details.newDirector' : 'admin.audit.details.director')}`;
    case 'CHOIR_RENAMED':
      return `${str('from')} → ${str('to')}`;
    case 'DIRECTOR_CHANGED':
      return [str('choir'), d.previous ? t('admin.audit.details.previous', { names: str('previous') }) : null, d.newDirector ? t('admin.audit.details.newDirector') : null]
        .filter(Boolean)
        .join(' · ');
    case 'FEEDBACK_DELETED':
      return [
        d.kind ? t(`admin.feedback.kinds1.${str('kind')}`) : null,
        d.sentAt ? t('admin.audit.details.sent', { date: formatDate(str('sentAt'), locale) }) : null,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'USER_DELETED':
      return t('admin.audit.details.userDeleted', {
        submissions: Number(d.submissions ?? 0),
        grades: Number(d.grades ?? 0),
        comments: Number(d.comments ?? 0),
        replies: Number(d.eventReplies ?? 0),
      });
    case 'RESET_BEFORE_LAUNCH':
      return t('admin.audit.details.reset', {
        choirs: Array.isArray(d.deletedChoirs) ? (d.deletedChoirs as string[]).join(', ') || '–' : '–',
        accounts: Number(d.accounts ?? 0),
        songs: Number(d.songs ?? 0),
        submissions: Number(d.submissions ?? 0),
      });
    case 'ADMIN_SIGN_IN':
      return t(d.setup ? 'admin.audit.details.firstSignIn' : 'admin.audit.details.twoStep');
    default:
      return '–';
  }
}

/** The audit log (wireframe 14): every view of another user's data and every administrator change. */
export function AdminAuditPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<AuditKind | null>(null);
  const [days, setDays] = useState(30);
  const log = useAuditLog(query, kind, days);
  const all = useAuditLog(query, null, days);
  const counts = all.data ?? [];
  const locale = dateLocale();
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' });
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className={`${ui.page} ${styles.page}`}>
      <div className={ui.pageHeader}>
        <div>
          <h1>{t('admin.audit.title')}</h1>
          <div className={ui.subtitle}>{t('admin.audit.subtitle')}</div>
        </div>
        <a className={ui.button} href={`/api/admin/audit.csv?${auditParams(query, kind, days)}`} download data-testid="audit-csv">
          {t('admin.audit.csv')}
        </a>
      </div>
      <div className={styles.listColumn}>
        <div className={styles.auditFilters}>
          <input
            className={ui.input}
            type="search"
            placeholder={t('admin.audit.search')}
            aria-label={t('admin.audit.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <label className={styles.inlineField}>
            <span className={ui.muted}>{t('admin.audit.date')}</span>
            <select className={ui.select} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {PERIODS.map((p) => (
                <option key={p} value={p}>
                  {p === 0 ? t('admin.audit.allTime') : t('admin.audit.lastDays', { count: p })}
                </option>
              ))}
            </select>
          </label>
        </div>
        <FilterChips
          label={t('admin.audit.kind')}
          value={kind}
          onChange={setKind}
          testId="audit-kind-filters"
          options={[
            { key: null, label: t('events.all'), count: counts.length },
            ...KINDS.map((k) => ({ key: k, label: t(`admin.audit.kinds.${k}`), count: counts.filter((e) => e.kind === k).length })),
          ]}
        />
        <section className={ui.card}>
          {log.isError && <ErrorView onRetry={() => void log.refetch()} />}
          {log.isPending && <p className={ui.muted}>{t('common.loading')}</p>}
          {log.data && log.data.length === 0 && <p className={ui.muted}>{t('admin.audit.none')}</p>}
          {log.data && log.data.length > 0 && (
            <table className={ui.table} data-testid="audit-table">
              <thead>
                <tr>
                  <th>{t('admin.audit.colTime')}</th>
                  <th>{t('admin.audit.colAction')}</th>
                  <th>{t('admin.audit.colWhose')}</th>
                  <th>{t('admin.audit.colDetails')}</th>
                </tr>
              </thead>
              <tbody>
                {log.data.map((e) => (
                  <tr key={e.id} data-testid="audit-row" data-action={e.action}>
                    <td className={styles.time}>
                      <strong>{time.format(new Date(e.at))}</strong>
                      <span className={ui.secondary}>{day.format(new Date(e.at))}</span>
                    </td>
                    <td>
                      <div>{t(`admin.audit.actions.${e.action}`, { name: firstName(e.subjectName), defaultValue: e.action })}</div>
                      <span className={ui.secondary}>
                        {t(`admin.audit.kind1.${e.kind}`)}
                        {e.viewAs && <> · {t('admin.audit.asName', { name: firstName(e.subjectName) })}</>}
                      </span>
                    </td>
                    <td>
                      {e.subjectName
                        ? e.action === 'USER_DELETED'
                          ? t('admin.audit.deletedUser', { name: e.subjectName })
                          : e.subjectName
                        : '–'}
                    </td>
                    <td className={ui.muted}>{auditDetails(e, t)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        <p className={styles.footnote}>{t('admin.audit.footnote')}</p>
      </div>
    </div>
  );
}
